/* eslint-disable prefer-template */
import React from "react";

import { Extension } from "..";
import { ResizeObserver as ResizeObserverPolyfill } from "@juggle/resize-observer";
import { intersection, union } from "lodash";
import { Mark, ResolvedPos } from "prosemirror-model";
import { NodeSelection, Plugin, PluginKey, Selection, TextSelection } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { v4 } from "uuid";

import { isCtrlOrCmd } from "~/lib/utils";
import { toast } from "~/ui";

import { insertLinkPillTr } from "../nodes/linkPill/linkPill";
import { getContext } from "../plugins/context";
import { attemptToEmbed, detectService, launchEmbedModal } from "../plugins/integrations/helpers";
import { reactRender } from "../plugins/reactHost";
import { getMarkBoundary } from "../utils";
import { DumbLinkToolBar } from "./linkTools";

const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;

const LinkToolsPluginKey = new PluginKey("link-tools");

function LinkMarkToolBar({ view, linkMark, ext }: { view: EditorView; ext: LinkTools; linkMark: Mark }) {
	const {
		doc,
		selection: { $from, $to, from, to },
	} = React.useMemo(() => {
		const {
			doc,
			selection: { $from, $to, from, to },
		} = view.state;
		return { doc, selection: { $from, $to, from, to } };
	}, [view.state]);
	const unlinkHandler = React.useCallback(() => {
		const [start, end] = getMarkBoundary(from, to, linkMark, doc);
		const tr = view.state.tr;
		view.dispatch(tr.removeMark(start, end, linkMark).scrollIntoView());
	}, [view, from, to, doc, linkMark]);
	const onConvertToPill = React.useCallback(() => {
		const [start, end] = getMarkBoundary(from, to, linkMark, doc);
		const tr = view.state.tr;
		const linkFrag = doc.slice(start, end).content;
		let currentText = linkFrag.textBetween(0, linkFrag.size);
		if (currentText === linkMark.attrs.url) {
			currentText = "";
		}
		insertLinkPillTr(tr, linkMark.attrs.url, start, end, currentText || undefined);
		view.dispatch(tr.scrollIntoView());
	}, [view, linkMark, doc, from, to]);
	const onConvertToEmbed = React.useCallback(() => {
		if (!detectService(linkMark.attrs.url)) {
			toast.info("Embedding of this URL is not supported.");
			return;
		}
		const [start, end] = getMarkBoundary(from, to, linkMark, doc);
		attemptToEmbed(view, linkMark.attrs.url, false, [start, end]);
	}, [view, linkMark, from, to, doc]);
	const submitHandler = React.useCallback(
		(url: string, text: string) => {
			const tr = view.state.tr;
			let newLink = linkMark;
			const [start, end] = getMarkBoundary(from, to, linkMark, doc);
			if (url !== linkMark.attrs.url) {
				tr.removeMark(start, end, linkMark);
				if (url) {
					newLink = linkMark.type.create({ ...linkMark.attrs, url, entityId: v4() });
					tr.addMark(start, end, newLink);
				}
			}
			const slice = doc.slice(start, end);
			const currentText = slice.content.textBetween(0, slice.size);
			let newText = text;
			if (!newText) {
				newText = newLink.attrs.url;
			}
			if (newText !== currentText) {
				let marks: Mark[] = [];
				if (from + 1 === to || from === to) {
					marks = union($to.marks(), $from.marks(), $from.nodeAfter ? $from.nodeAfter.marks : []);
				} else {
					marks = intersection(doc.resolve(from + 1).marks(), doc.resolve(to - 1).marks());
				}
				tr.replaceRangeWith(start, end, view.state.schema.text(newText).mark(newLink.addToSet(marks)));
			}
			if (tr.steps.length) {
				tr.scrollIntoView();
				if (tr.selection) {
					tr.setSelection(new TextSelection(tr.selection.$to));
				}
				view.dispatch(tr);
				view.focus();
			}
		},
		[view, from, to, linkMark, $from, doc, $to],
	);
	const text = React.useMemo(() => {
		const [start, end] = getMarkBoundary(from, to, linkMark, doc);
		const slice = doc.slice(start, end);
		return slice.content.textBetween(0, slice.size);
	}, [doc, from, to, linkMark]);
	return (
		<DumbLinkToolBar
			editShowText={true}
			displayMode="text"
			canCopy
			key={`${linkMark.attrs.entityId}:${linkMark.attrs.url}`} // reset state when entityId or url changes
			url={linkMark.attrs.url}
			text={text === linkMark.attrs.url ? "" : text}
			insertingNewLink={text === "" && linkMark.attrs.url === ""}
			onEditSubmit={submitHandler}
			onUnlink={unlinkHandler}
			onConvertToPill={"link_pill" in view.state.schema.nodes ? onConvertToPill : undefined}
			onConvertToEmbed={ext.allowEmbeds ? onConvertToEmbed : undefined}
			captureMouseDown={ext.captureMouseDown}
		/>
	);
}

function LinkPillToolbar({ view, linkPillPos, ext }: { view: EditorView; ext: LinkTools; linkPillPos: ResolvedPos }) {
	const node = linkPillPos.nodeAfter;
	if (!node) {
		throw new Error("No node");
	}
	const { url, entityId } = node.attrs;
	const text = node.textContent;
	const onConvertToMark = React.useCallback(() => {
		let tr = view.state.tr;
		const mark = view.state.schema.mark("link", { url, entityId: v4() });
		const currentText = node.textContent;
		const textNode = view.state.schema.text(currentText);
		tr.replaceWith(linkPillPos.pos, linkPillPos.pos + node.nodeSize, textNode);
		tr.addMark(linkPillPos.pos, linkPillPos.pos + textNode.nodeSize, mark);
		view.dispatch(tr.setSelection(Selection.atStart(tr.doc))); // we want to close the toolbar after converting
	}, [view, linkPillPos, url, node]);
	const onEditSubmit = React.useCallback(
		(newUrl: string, newText: string) => {
			if ((!newUrl && !newText) || (newUrl === url && newText === text)) return;
			let tr = view.state.tr;
			tr = insertLinkPillTr(tr, newUrl, linkPillPos.pos, linkPillPos.pos + node.nodeSize, newText);
			view.dispatch(tr);
		},
		[view, linkPillPos, url, node, text],
	);
	const onConvertToEmbed = React.useCallback(() => {
		if (!detectService(node.attrs.url)) {
			toast.info("Embedding of this URL is not supported.");
			return;
		}
		attemptToEmbed(view, node.attrs.url, false, [linkPillPos.pos, linkPillPos.pos + node.nodeSize]);
	}, [view, node, linkPillPos]);
	const onRemove = React.useCallback(() => {
		let tr = view.state.tr;
		tr.deleteRange(linkPillPos.pos, linkPillPos.pos + node.nodeSize);
		view.dispatch(tr);
	}, [view, node, linkPillPos]);
	if (!url) return null;
	return (
		<DumbLinkToolBar
			editShowText
			displayMode="linkPill"
			canCopy
			key={`${entityId}:${url}`} // reset state when entityId or url changes
			url={url}
			text={text}
			onRemove={onRemove}
			onConvertToText={onConvertToMark}
			onEditSubmit={onEditSubmit}
			onConvertToEmbed={ext.allowEmbeds ? onConvertToEmbed : undefined}
			captureMouseDown={ext.captureMouseDown}
		/>
	);
}

function EmbedToolbar({ view, embedBlockPos, ext }: { view: EditorView; embedBlockPos: ResolvedPos; ext: LinkTools }) {
	const node = embedBlockPos.nodeAfter;
	if (!node) {
		throw new Error("No node");
	}
	const { originalUrl, blockId } = node.attrs;
	const onConvertToMark = React.useCallback(() => {
		let tr = view.state.tr;
		const mark = view.state.schema.mark("link", { url: originalUrl, entityId: v4() });
		const textNode = view.state.schema.text(originalUrl);
		const unstyledBlock = view.state.schema.nodes["unstyled"].createChecked({ blockId: v4() }, textNode);
		const start = embedBlockPos.pos;
		tr.replaceWith(start, start + node.nodeSize, unstyledBlock);
		tr.addMark(start + 1, start + 1 + textNode.nodeSize, mark);
		view.dispatch(tr.setSelection(Selection.atStart(tr.doc))); // we want to close the toolbar after converting
	}, [view, embedBlockPos, originalUrl, node]);
	const onConvertToPill = React.useCallback(() => {
		let tr = view.state.tr;
		const pillNode = view.state.schema.nodes["link_pill"].createAndFill(
			{ entityId: v4(), url: originalUrl },
			view.state.schema.text(originalUrl),
		);
		const unstyledBlock = view.state.schema.nodes["unstyled"].createAndFill({ blockId: v4() }, pillNode);
		if (!unstyledBlock) {
			return;
		}
		const start = embedBlockPos.pos;
		tr.replaceWith(start, start + node.nodeSize, unstyledBlock);
		view.dispatch(tr);
	}, [view, embedBlockPos, originalUrl, node]);
	const onEditClick = React.useCallback(() => {
		const getPos = () => {
			return embedBlockPos.pos;
		};
		launchEmbedModal(view, {
			nodeToReplace: { blockId: node.attrs.blockId, getPos },
			service: node.attrs.service,
		});
	}, [node, view, embedBlockPos]);
	const onRemove = React.useCallback(() => {
		let tr = view.state.tr;
		tr.deleteRange(embedBlockPos.pos, embedBlockPos.pos + node.nodeSize);
		view.dispatch(tr);
	}, [view, node, embedBlockPos]);
	if (!originalUrl) return null;
	return (
		<DumbLinkToolBar
			editShowText={false}
			displayMode="embedded"
			canCopy
			key={`${blockId}:${originalUrl}`} // reset state when entityId or url changes
			url={originalUrl}
			text={originalUrl}
			onConvertToText={onConvertToMark}
			onEditClick={onEditClick}
			onConvertToPill={onConvertToPill}
			onRemove={onRemove}
			captureMouseDown={ext.captureMouseDown}
		/>
	);
}

function renderToolbarForTextLink(ext: LinkTools, view: EditorView, linkMark: Mark) {
	reactRender(view, ext.toolBarDOM, <LinkMarkToolBar ext={ext} view={view} linkMark={linkMark} />, true);
}

function renderToolbarForLinkPill(ext: LinkTools, view: EditorView, linkPillPos: ResolvedPos) {
	return reactRender(view, ext.toolBarDOM, <LinkPillToolbar ext={ext} view={view} linkPillPos={linkPillPos} />, true);
}

function renderToolBarForEmbed(ext: LinkTools, view: EditorView, embedBlockPos: ResolvedPos) {
	return reactRender(view, ext.toolBarDOM, <EmbedToolbar view={view} embedBlockPos={embedBlockPos} ext={ext} />, true);
}

function makeToolbarPlugin(ext: LinkTools): Plugin {
	return new Plugin({
		key: LinkToolsPluginKey,
		props: {},
		view: (ev: EditorView) => {
			return {
				update: (view: EditorView, _prevState) => {
					const {
						state: { selection, doc },
					} = view;
					const isLocked = getContext(ev.state).locked;
					if (isLocked) {
						ext.removeToolbar();
						return;
					}
					const { $from, $to, from, to } = selection;
					let marks: Mark[] = [];
					if (from + 1 === to || from === to) {
						marks = union($to.marks(), $from.marks(), $from.nodeAfter ? $from.nodeAfter.marks : []);
					} else {
						marks = intersection(doc.resolve(from + 1).marks(), doc.resolve(to - 1).marks());
					}
					const linkMark = marks.find(m => m.type.name === "link");
					let shouldShow = false;

					// It turned out, that even if there is no cursor in the row, the `selection.from` and `selection.to`
					// are always = 1. So the solution is simple - do not open the toolbar if selection pos = 1.
					if (linkMark && !(from === 1 && to === 1)) {
						renderToolbarForTextLink(ext, view, linkMark);
						shouldShow = true;
					}

					let linkPillPos: ResolvedPos | null = null;

					if (selection instanceof NodeSelection && selection.node.type.name === "link_pill") {
						linkPillPos = selection.$from;
					}

					if (linkPillPos) {
						renderToolbarForLinkPill(ext, view, linkPillPos);
						shouldShow = true;
					}

					let embedBlockPos: ResolvedPos | null = null;

					if (ext.allowEmbeds && selection instanceof NodeSelection && selection.node.type.name === "embed") {
						embedBlockPos = selection.$from;
					}

					if (embedBlockPos) {
						renderToolBarForEmbed(ext, view, embedBlockPos);
						shouldShow = true;
					}

					const storedMarks = view.state.storedMarks ?? [];
					const storedLinkMark = storedMarks.find(m => m.type.name === "link");

					if (storedLinkMark) {
						renderToolbarForTextLink(ext, view, storedLinkMark);
						shouldShow = true;
					}

					if (!shouldShow) {
						ext.removeToolbar();
					} else {
						ext.showToolbar();
						ext.updateToolBarPosition();
					}
				},
				destroy() {
					ext.removeToolbar();
				},
			};
		},
	});
}

export class LinkTools extends Extension {
	toolBarDOM: HTMLDivElement;
	allowEditing: boolean;
	allowEmbeds: boolean;
	resizeObserver: ResizeObserver;
	captureMouseDown: boolean;
	constructor(options = { allowEditing: true, allowEmbeds: true, captureMouseDown: false }) {
		super();
		this.allowEditing = options.allowEditing;
		this.allowEmbeds = options.allowEmbeds; // this will be forced to false if we don't have a embed type in the schema see init()
		this.captureMouseDown = options.captureMouseDown;
		this.toolBarDOM = document.createElement("div");
		this.toolBarDOM.setAttribute("portal-id", v4());
		this.toolBarDOM.classList.add("prose-link-toolbar-wrapper");
		this.resizeObserver = new ResizeObserver(() => {
			if (!this.editorView) return;
			if (this.toolBarDOM.parentElement === this.editorView.dom.parentElement) {
				this.updateToolBarPosition();
			}
		});
	}
	get name() {
		return "link-tools";
	}
	get plugins() {
		if (!this.allowEditing) return [];
		return [makeToolbarPlugin(this)];
	}
	private watchDOMNodeChange(node: HTMLElement) {
		this.resizeObserver.disconnect();
		this.resizeObserver.observe(node);
	}
	updateToolBarPosition() {
		// NOTE toolbarDOM is relative to parentBox and has translateX(-50%)
		const { editorView: view } = this;
		if (!view) return;
		const { selection } = view.state;
		const { from, to } = selection;
		const parentElement = view.dom.parentElement;
		if (!parentElement) return;
		const parentBox = parentElement.getBoundingClientRect();
		const parentScrollTop = parentElement.scrollTop;
		const coord = view.coordsAtPos(Math.floor((from + to) / 2));
		const toolbarWidth = this.toolBarDOM.clientWidth;
		let left: number;
		if (!(selection instanceof NodeSelection)) {
			let topPosition = coord.top + parentScrollTop - parentBox.top;
			let top = `calc(${topPosition}px + 1.5rem)`;
			// Check if it overflows in the bottom
			const bottomMargin = 32;
			if (topPosition + this.toolBarDOM.clientHeight + bottomMargin > parentBox.height) {
				topPosition -= this.toolBarDOM.clientHeight;
				top = `calc(${topPosition}px`;
			}
			this.toolBarDOM.style.top = top;
			left = Math.max(toolbarWidth / 2, coord.left - parentBox.left);
		} else {
			const fromCoord = view.coordsAtPos(from);
			let nodeDOM = view.nodeDOM(from);
			if (!(nodeDOM instanceof HTMLElement)) {
				throw new Error("Getting the wrong node");
			}
			const nodeRect = nodeDOM.getBoundingClientRect();
			let attachedNodeHeight = nodeRect.height;
			// special case the position for embeds
			const embedMenuBar = nodeDOM.querySelector("[data-embed-menu-bar]");
			if (embedMenuBar) {
				attachedNodeHeight = embedMenuBar.getBoundingClientRect().height;
			}
			this.toolBarDOM.style.top = `${coord.top + parentScrollTop - parentBox.top + attachedNodeHeight + 4}px`;
			left = Math.max(
				toolbarWidth / 2,
				fromCoord.left - parentBox.left + toolbarWidth / 2,
				nodeRect.left + nodeRect.width / 2 - parentBox.left,
			);
		}
		// clamp to parentBox.
		if (left + toolbarWidth / 2 > parentBox.width) {
			left = parentBox.width - toolbarWidth / 2;
		}
		this.toolBarDOM.style.left = `${left}px`;
	}
	showToolbar() {
		if (!this.editorView) return;
		if (this.toolBarDOM.parentElement != this.editorView.dom.parentElement) {
			(this.editorView.dom.parentElement as HTMLElement).appendChild(this.toolBarDOM);
		}
	}
	removeToolbar() {
		if (!this.editorView) return;
		reactRender(this.editorView, this.toolBarDOM, null, true);
		this.toolBarDOM.remove();
		this.resizeObserver.disconnect();
	}
	init(): void {
		if (!this.editorView) return;
		this.editorView.dom.addEventListener("click", this.stopLinkClick, { capture: true });
		if (this.allowEmbeds && !this.editorView.state.schema.nodes["embed"]) {
			this.allowEmbeds = false;
		}
	}
	destroy(): void {
		this.removeToolbar();
		if (!this.editorView) return;
		this.editorView.dom.removeEventListener("click", this.stopLinkClick, { capture: true });
	}
	private stopLinkClick = (event: MouseEvent) => {
		if (
			this.editorView?.editable &&
			event.target instanceof HTMLElement &&
			event.target.closest("[data-link-pill]") &&
			event.button === 0 &&
			!isCtrlOrCmd(event)
		) {
			event.preventDefault();
		}
	};
}
